import { useCallback, useState } from "react";

import { toast } from "components/ui/use-toast";

import { downloadFromBlob } from "@/utils/download-blob";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import type { BlobDownload } from "@/utils/blob-response-handler";
import {
  useDownloadExcelMutation,
  type DownloadExcelResponse,
} from "@/services/tax-closing";

import { TAX_CLOSING_TOASTS, type ToastPayload } from "../messages";

const MAX_ATTEMPTS = 15;
const RETRY_DELAY_MS = 4000;
const HTTP_CONFLICT = 409;
const HTTP_NOT_FOUND = 404;
const FALLBACK_FILENAME = "fechamento_fiscal.xlsx";
const NO_MESSAGE = "";

interface ApiError {
  status?: number | string;
  data?: { message?: string; messages?: string[] };
}

export interface UseExcelDownloadResult {
  exportExcel: () => Promise<void>;
  downloading: boolean;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

function isBlobDownload(result: DownloadExcelResponse): result is BlobDownload {
  const candidate: BlobDownload | null = result as BlobDownload | null;
  return candidate?.blob instanceof Blob && candidate.blob.size > 0;
}

function exportErrorToast(err: ApiError): ToastPayload {
  if (err?.status === HTTP_NOT_FOUND) {
    return TAX_CLOSING_TOASTS.excelNotAvailable();
  }
  if (typeof err?.status !== "number") {
    return TAX_CLOSING_TOASTS.excelExportGenericError();
  }
  const message: string = extractRtkErrorMessage(err, NO_MESSAGE);
  if (!message) {
    return TAX_CLOSING_TOASTS.excelExportGenericError();
  }
  return TAX_CLOSING_TOASTS.excelExportError(message);
}

export function useExcelDownload(
  jobId: string | undefined,
): UseExcelDownloadResult {
  const [triggerDownload, { reset }] = useDownloadExcelMutation();
  const [downloading, setDownloading] = useState<boolean>(false);

  const exportExcel = useCallback(async (): Promise<void> => {
    if (!jobId) return;
    setDownloading(true);
    let generatingToastShown: boolean = false;
    try {
      for (let attempt: number = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const result: DownloadExcelResponse =
            await triggerDownload(jobId).unwrap();
          if (!isBlobDownload(result)) {
            toast(TAX_CLOSING_TOASTS.excelEmptyResponse());
            return;
          }
          downloadFromBlob(result.blob, result.filename || FALLBACK_FILENAME);
          return;
        } catch (err) {
          const apiErr: ApiError = err as ApiError;
          if (apiErr?.status !== HTTP_CONFLICT) {
            toast(exportErrorToast(apiErr));
            return;
          }
          if (!generatingToastShown) {
            toast(TAX_CLOSING_TOASTS.excelGenerating());
            generatingToastShown = true;
          }
          if (attempt < MAX_ATTEMPTS) await delay(RETRY_DELAY_MS);
        }
      }
      toast(TAX_CLOSING_TOASTS.excelExportTimeout());
    } finally {
      setDownloading(false);
      reset();
    }
  }, [jobId, triggerDownload, reset]);

  return { exportExcel, downloading };
}
